import { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import DocHeader from '../components/docs/DocHeader';
import DocNav, { DocItem } from '../components/docs/DocNav';
import TableOfContents, { TocItem } from '../components/docs/TableOfContents';
import MarkdownRenderer from '../components/docs/MarkdownRenderer';

export default function DocViewer() {
  const { org, collection, slug } = useParams<{ org: string; collection: string; slug: string }>();
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [docTitle, setDocTitle] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [requiresAuth, setRequiresAuth] = useState(false);
  const [allDocs, setAllDocs] = useState<DocItem[]>([]);
  const [zoom, setZoom] = useState(() => {
    const saved = localStorage.getItem('doc-zoom');
    return saved ? Number(saved) : 100;
  });
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLElement>(null);
  const scrollContainerRef = useRef<HTMLElement>(null);
  const [tocItems, setTocItems] = useState<TocItem[]>([]);

  useEffect(() => {
    localStorage.setItem('doc-zoom', String(zoom));
  }, [zoom]);

  useEffect(() => {
    if (!content || !contentRef.current) return;
    const timer = setTimeout(() => {
      const headings = contentRef.current?.querySelectorAll('h2[id], h3[id]');
      if (!headings) return;
      const items: TocItem[] = Array.from(headings).map((heading) => ({
        id: heading.id,
        text: heading.textContent?.trim() || '',
        depth: parseInt(heading.tagName[1], 10),
      }));
      setTocItems(items);
    }, 100);
    return () => clearTimeout(timer);
  }, [content]);

  useEffect(() => {
    if (authLoading) return;
    if (!org || !collection || !slug) {
      setError('Missing required parameters');
      setLoading(false);
      return;
    }

    async function loadDoc() {
      setRequiresAuth(false);
      setError(null);
      try {
        const fetch = session?.user
          ? api.docs.get(org!, collection!, slug!)
          : api.docs.getPublic(org!, collection!, slug!);

        const data = await fetch;

        const docs: DocItem[] = data.documents.map(d => ({
          id: d.slug,
          title: d.title,
          order_index: d.display_order,
          content: '',
        }));
        setAllDocs(docs);
        setDocTitle(data.document.title);
        setContent(data.document.content);
        setCollectionName(collection!);
      } catch (err: any) {
        if (err?.status === 401 || err?.status === 403) {
          setRequiresAuth(true);
        } else {
          setError(err?.message ?? 'Failed to load document');
        }
      } finally {
        setLoading(false);
      }
    }

    loadDoc();
  }, [org, collection, slug, session, authLoading]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading document...</p>
        </div>
      </div>
    );
  }

  if (requiresAuth) {
    const currentPath = window.location.pathname;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-2">Authentication Required</h1>
          <p className="text-muted-foreground mb-6">
            This documentation is private. Please sign in to access it.
          </p>
          <Link
            to={`/auth/login?redirect=${encodeURIComponent(currentPath)}`}
            className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition"
          >
            Sign in to continue
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page flex flex-col">
      <DocHeader org={org!} collection={collection!} />

      <div className="flex flex-1">
        <DocNav
          org={org!}
          collection={collection!}
          collectionName={collectionName}
          docs={allDocs}
          currentDocId={slug}
          zoom={zoom}
          onZoomChange={setZoom}
        />

        <main ref={scrollContainerRef} className="flex-1 min-w-0 overflow-auto py-6 px-4">
          <div className="max-w-4xl mx-auto bg-background rounded-xl shadow-sm px-8 py-8">
            <nav className="text-sm text-muted-foreground mb-8">
              <span>{org}</span>
              <span className="mx-2">/</span>
              <span>{collection}</span>
              <span className="mx-2">/</span>
              <span className="text-foreground font-medium">{docTitle}</span>
            </nav>

            <article
              ref={contentRef}
              id="doc-content"
              style={{
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top left',
                width: `${10000 / zoom}%`,
              }}
            >
              <MarkdownRenderer content={content} />
            </article>
          </div>
        </main>

        <aside className="w-64 p-4 hidden lg:block h-[calc(100vh-3.5rem)] sticky top-14 overflow-y-auto no-print">
          <div className="bg-background rounded-xl shadow-sm p-4">
            <TableOfContents items={tocItems} scrollContainer={scrollContainerRef} />
          </div>
        </aside>
      </div>
    </div>
  );
}
